/**
 * Fermentation custom variant picker
 * Shared SKU/state utilities + custom configurator UI.
 */

(function() {
  'use strict';

  const JAR_COUNT = 4;
  const DEFAULT_JARS = ['B', 'B', 'B', 'B'];
  const QUICK_VIEW_SELECTORS = [
    'quick-view-drawer details[open]',
    'quick-view-drawer details.menu-opening',
    '.quick-view--open',
    '.quick-view--opening'
  ];

  const getScopeRoot = function() {
    for (let i = 0; i < QUICK_VIEW_SELECTORS.length; i += 1) {
      const hit = document.querySelector(QUICK_VIEW_SELECTORS[i]);
      if (hit) return hit;
    }
    return document;
  };

  const qsa = (selector, root = null) => Array.from((root || getScopeRoot()).querySelectorAll(selector));
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const DOMHelper = {
    getElement: function(selector, root = null) {
      const scope = root || getScopeRoot();
      return scope.querySelector(selector) || null;
    },

    getElements: function(selector, root = null) {
      const scope = root || getScopeRoot();
      return scope.querySelectorAll(selector);
    },

    createElement: function(tagName, options = {}) {
      const customizedIs = options.is || (options.attributes && options.attributes.is);
      let element;
      if (customizedIs) {
        try {
          element = document.createElement(tagName, { is: customizedIs });
        } catch (_error) {
          element = document.createElement(tagName);
        }
      } else {
        element = document.createElement(tagName);
      }

      if (options.className) element.className = options.className;
      if (options.attributes) {
        Object.entries(options.attributes).forEach(([key, value]) => {
          if (key === 'is') return;
          element.setAttribute(key, value);
        });
      }
      if (customizedIs && !element.getAttribute('is')) {
        try { element.setAttribute('is', customizedIs); } catch (_error) {}
      }
      if (options.text != null) element.textContent = options.text;
      if (options.html != null) element.innerHTML = options.html;
      if (options.styles) {
        Object.entries(options.styles).forEach(([key, value]) => {
          element.style[key] = value;
        });
      }
      return element;
    }
  };

  const getMergedContainer = function(root = null) {
    const scope = root || getScopeRoot();
    return scope.querySelector('.fermentation-merged-picker');
  };

  const getVariantSelect = function(root = null) {
    const scope = root || getScopeRoot();
    return (
      scope.querySelector('variant-selects[data-primary] select[id^="Option-"][name^="options["]') ||
      scope.querySelector('select[id^="Option-"][name^="options["]') ||
      null
    );
  };

  const dispatchChange = function(element) {
    if (!element) return;
    try {
      element.dispatchEvent(new Event('change', { bubbles: true }));
    } catch (_error) {
      try {
        const legacyEvent = document.createEvent('Event');
        legacyEvent.initEvent('change', true, true);
        element.dispatchEvent(legacyEvent);
      } catch (_legacyError) {}
    }
  };

  const parseConfig = function(host) {
    const node = host ? host.querySelector('[data-fermentation-picker-config]') : null;
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (_error) {
      return null;
    }
  };

  const getVariantData = function(config, sku) {
    if (!config || !config.variants || !sku) return null;
    return config.variants[sku] || null;
  };

  const isVariantExisting = function(config, sku) {
    const variant = getVariantData(config, sku);
    return !!(variant && variant.exists);
  };

  const isVariantSelectable = function(config, sku) {
    const variant = getVariantData(config, sku);
    return !!(variant && variant.exists && variant.available);
  };

  const resolveJarAvailability = function(config, jarCode) {
    const key = String(jarCode || '').toUpperCase();
    if (!key) return null;

    const configJar = config && config.jarAvailability ? config.jarAvailability[key] : null;
    const configAvailable = configJar && typeof configJar.available === 'boolean' ? configJar.available : null;

    const fermentationGlobal = window.fermentationJarAvailability && window.fermentationJarAvailability[key];
    const fermentationGlobalAvailable = fermentationGlobal && typeof fermentationGlobal.available === 'boolean'
      ? fermentationGlobal.available
      : null;

    const ecosealCode = key === 'B' ? 'MM' : key === 'C' ? 'ML' : '';
    const ecosealGlobal = ecosealCode && window.ecosealJarAvailability ? window.ecosealJarAvailability[ecosealCode] : null;
    const ecosealAvailable = ecosealGlobal && typeof ecosealGlobal.available === 'boolean'
      ? ecosealGlobal.available
      : null;

    // Prefer live/global sources first, then per-picker config.
    if (ecosealAvailable !== null) return ecosealAvailable;
    if (fermentationGlobalAvailable !== null) return fermentationGlobalAvailable;
    return configAvailable;
  };

  const isJarSoldOut = function(config, jarCode) {
    const availability = resolveJarAvailability(config, jarCode);
    // Ecoseal parity: only block when we know for sure it's unavailable.
    return availability === false;
  };

  const isStampferSoldOut = function(config) {
    const addon = config && config.stampferAddon;
    return !!(addon && addon.exists && addon.available === false);
  };

  const normalizeJars = function(jars) {
    const source = Array.isArray(jars) ? jars : [];
    const normalized = source
      .map((jar) => String(jar || '').toUpperCase())
      .filter((jar) => jar === 'B' || jar === 'C')
      .slice(0, JAR_COUNT);

    while (normalized.length < JAR_COUNT) normalized.push('B');
    return normalized;
  };

  const normalizeState = function(state) {
    return {
      noJars: !!(state && state.noJars),
      jars: normalizeJars(state ? state.jars : DEFAULT_JARS),
      tamper: Number(state && state.tamper ? 1 : 0)
    };
  };

  const sanitizeStateForStampfer = function(config, state) {
    const normalized = normalizeState(state);
    if (isStampferSoldOut(config) && normalized.tamper === 1) {
      return { ...normalized, tamper: 0 };
    }
    return normalized;
  };

  const encodeSku = function(state) {
    const normalized = normalizeState(state);
    const tamperSuffix = normalized.tamper === 1 ? '_T1' : '';

    if (normalized.noJars) {
      return `FERMSET${tamperSuffix}`;
    }

    const sortedLetters = normalized.jars.slice().sort().join('');
    return `FERMSET_${JAR_COUNT}${sortedLetters}${tamperSuffix}`;
  };

  const decodeSku = function(sku) {
    const raw = String(sku || '').trim().toUpperCase();
    if (!raw) return null;

    if (raw === 'FERMSET' || raw === 'FERMSET_T1') {
      return normalizeState({
        noJars: true,
        jars: DEFAULT_JARS,
        tamper: raw.endsWith('_T1') ? 1 : 0
      });
    }

    const matched = /^FERMSET_(\d+)([BC]+?)(?:_T1)?$/.exec(raw);
    if (!matched) return null;

    const quantity = Number(matched[1] || 0);
    const letters = String(matched[2] || '').split('');
    if (!quantity || quantity !== letters.length) return null;

    return normalizeState({
      noJars: false,
      jars: letters,
      tamper: raw.endsWith('_T1') ? 1 : 0
    });
  };

  const getMainStateByKey = function(config, key) {
    if (!config || !key) return null;

    if (config.mainSetStates && config.mainSetStates[key]) {
      return normalizeState(config.mainSetStates[key]);
    }

    const sku = config.mainSetSkus && config.mainSetSkus[key] ? config.mainSetSkus[key] : '';
    if (!sku) return null;
    return decodeSku(sku);
  };

  const areStatesEquivalent = function(leftState, rightState) {
    if (!leftState || !rightState) return false;
    const left = normalizeState(leftState);
    const right = normalizeState(rightState);

    if (left.noJars !== right.noJars) return false;
    if (left.tamper !== right.tamper) return false;
    if (left.noJars) return true;

    const leftSorted = left.jars.slice().sort().join('');
    const rightSorted = right.jars.slice().sort().join('');
    return leftSorted === rightSorted;
  };

  const resolveStateFromSku = function(config, sku) {
    const rawSku = String(sku || '').trim();
    if (!rawSku) return null;

    const decoded = decodeSku(rawSku);
    if (decoded) return decoded;

    const mainSetSkus = config && config.mainSetSkus ? config.mainSetSkus : null;
    if (mainSetSkus) {
      const directKey = Object.keys(mainSetSkus).find((key) => String(mainSetSkus[key] || '').trim() === rawSku);
      if (directKey) {
        const mappedState = getMainStateByKey(config, directKey);
        if (mappedState) return mappedState;
      }
    }

    if (config && Array.isArray(config.fixedSets)) {
      const set = config.fixedSets.find((entry) => entry && String(entry.sku || '').trim() === rawSku);
      if (set && set.state) return normalizeState(set.state);
    }

    return null;
  };

  const findVariantById = function(config, root, variantId) {
    const select = getVariantSelect(root);
    if (!select || variantId == null) return null;
    const targetId = String(variantId);
    const option = Array.from(select.options).find((candidate) => String(candidate.dataset.variantId || '') === targetId);
    if (!option) return null;
    const sku = (option.dataset.variantSku || '').trim();
    return getVariantData(config, sku) ? sku : null;
  };

  const getCurrentSkuFromSelect = function(config, root) {
    const select = getVariantSelect(root);
    if (!select || select.selectedIndex < 0) return '';
    const option = select.options[select.selectedIndex];
    const sku = (option.dataset.variantSku || '').trim();
    return getVariantData(config, sku) ? sku : '';
  };

  const getCurrentVariant = function(root) {
    const scope = root || getScopeRoot();

    try {
      const url = new URL(window.location.href);
      const variantId = url.searchParams.get('variant');
      if (variantId) {
        const select = getVariantSelect(scope);
        const option = select ? Array.from(select.options).find((candidate) => String(candidate.dataset.variantId || '') === String(variantId)) : null;
        if (option) {
          return {
            id: Number(option.dataset.variantId),
            sku: option.dataset.variantSku || ''
          };
        }
      }
    } catch (_error) {}

    const select = getVariantSelect(scope);
    if (select && select.selectedIndex >= 0) {
      const option = select.options[select.selectedIndex];
      if (option && option.dataset.variantId) {
        return {
          id: Number(option.dataset.variantId),
          sku: option.dataset.variantSku || ''
        };
      }
    }

    const idInput = scope.querySelector('input[name="id"]');
    if (idInput && idInput.value) {
      return { id: Number(idInput.value) };
    }

    return null;
  };

  const updateVariantBySku = function(sku, root) {
    const select = getVariantSelect(root);
    if (!select) return;
    const targetSku = String(sku || '').trim();
    if (!targetSku) return;

    const option = Array.from(select.options).find((candidate) => (candidate.dataset.variantSku || '').trim() === targetSku);
    if (!option) return;
    if (select.value === option.value) return;

    // Keep this aligned with EcoSeal: a single native change event drives Shopify updates.
    select.value = option.value;
    dispatchChange(select);
  };

  const getMainKeyBySku = function(config, sku) {
    if (!config || !config.mainSetSkus) return '';
    const rawSku = String(sku || '').trim();
    const directKey = Object.keys(config.mainSetSkus).find((key) => String(config.mainSetSkus[key] || '').trim() === rawSku);
    if (directKey) return directKey;

    const state = resolveStateFromSku(config, rawSku);
    if (!state) return '';

    const keys = Object.keys(config.mainSetSkus);
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const candidateState = getMainStateByKey(config, key);
      if (candidateState && areStatesEquivalent(state, candidateState)) return key;
    }
    return '';
  };

  const getMainCandidateStates = function(config) {
    if (!config || !config.mainSetSkus) return [];
    const seen = new Set();
    return Object.keys(config.mainSetSkus).reduce((acc, key) => {
      const state = getMainStateByKey(config, key);
      if (!state) return acc;
      const signature = `${state.noJars ? '1' : '0'}:${state.tamper}:${state.jars.slice().sort().join('')}`;
      if (seen.has(signature)) return acc;
      seen.add(signature);
      acc.push(state);
      return acc;
    }, []);
  };

  const getMainSkuForState = function(config, state) {
    if (!config || !config.mainSetSkus || !state) return '';
    const normalized = normalizeState(state);
    const keys = Object.keys(config.mainSetSkus);

    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const mainState = getMainStateByKey(config, key);
      if (mainState && areStatesEquivalent(normalized, mainState)) {
        return String(config.mainSetSkus[key] || '').trim();
      }
    }

    const fallbackKey = getMainKeyBySku(config, encodeSku(normalized));
    return fallbackKey ? String(config.mainSetSkus[fallbackKey] || '').trim() : '';
  };

  const resolveVariantSkuForState = function(config, state) {
    const normalized = normalizeState(state);
    const encoded = encodeSku(normalized);
    if (isVariantExisting(config, encoded)) return encoded;

    const mainSku = getMainSkuForState(config, normalized);
    if (mainSku && isVariantExisting(config, mainSku)) return mainSku;

    return encoded;
  };

  const resolvePreferredState = function(config, desiredState) {
    const normalized = sanitizeStateForStampfer(config, desiredState);
    const candidates = [normalized];

    if (!isStampferSoldOut(config)) {
      candidates.push({ ...normalized, tamper: normalized.tamper === 1 ? 0 : 1 });
    }

    for (let i = 0; i < candidates.length; i += 1) {
      const sku = resolveVariantSkuForState(config, candidates[i]);
      if (isVariantSelectable(config, sku)) return candidates[i];
    }
    for (let i = 0; i < candidates.length; i += 1) {
      const sku = resolveVariantSkuForState(config, candidates[i]);
      if (isVariantExisting(config, sku)) return candidates[i];
    }
    return null;
  };

  const resolveDefaultCarouselState = function(config) {
    const decodedDefault = resolveStateFromSku(config, config ? config.defaultSku : '');
    if (decodedDefault) {
      const resolvedDefault = resolvePreferredState(config, decodedDefault);
      if (resolvedDefault && getMainKeyBySku(config, encodeSku(resolvedDefault))) return resolvedDefault;
    }

    const mainStates = getMainCandidateStates(config);
    for (let i = 0; i < mainStates.length; i += 1) {
      const resolved = resolvePreferredState(config, mainStates[i]);
      if (resolved && getMainKeyBySku(config, encodeSku(resolved))) return resolved;
    }

    return sanitizeStateForStampfer(config, decodedDefault || normalizeState({ noJars: false, jars: DEFAULT_JARS, tamper: 1 }));
  };

  const getJarCounts = function(state) {
    if (!state || state.noJars) return { b: 0, c: 0 };
    const b = state.jars.filter((jar) => jar === 'B').length;
    return { b, c: JAR_COUNT - b };
  };

  const scoreMainStateMatch = function(targetState, candidateState) {
    if (!targetState || !candidateState) return Number.POSITIVE_INFINITY;
    const target = normalizeState(targetState);
    const candidate = normalizeState(candidateState);
    const targetCounts = getJarCounts(target);
    const candidateCounts = getJarCounts(candidate);

    const noJarPenalty = target.noJars === candidate.noJars ? 0 : 8;
    const jarPenalty = Math.abs(targetCounts.b - candidateCounts.b) + Math.abs(targetCounts.c - candidateCounts.c);
    const tamperPenalty = target.tamper === candidate.tamper ? 0 : 2;
    return noJarPenalty + jarPenalty + tamperPenalty;
  };

  const resolveCarouselStateFromCurrent = function(config, state) {
    const normalizedCurrent = sanitizeStateForStampfer(config, state);
    const directResolved = resolvePreferredState(config, normalizedCurrent);
    if (directResolved && getMainKeyBySku(config, encodeSku(directResolved))) return directResolved;

    const mainStates = getMainCandidateStates(config);
    let bestState = null;
    let bestScore = Number.POSITIVE_INFINITY;

    for (let i = 0; i < mainStates.length; i += 1) {
      const resolved = resolvePreferredState(config, mainStates[i]);
      if (!resolved) continue;
      if (!getMainKeyBySku(config, encodeSku(resolved))) continue;

      const score = scoreMainStateMatch(normalizedCurrent, resolved);
      if (score < bestScore) {
        bestScore = score;
        bestState = resolved;
      }
    }

    return bestState || resolveDefaultCarouselState(config);
  };

  const getJarSummaryLabel = function(config, state) {
    const labels = config && config.ui ? config.ui : {};
    if (state.noJars) return labels.noJarsLabel || '';

    const countB = state.jars.filter((jar) => jar === 'B').length;
    if (countB === 4) return labels.summary4bbbb || '';
    if (countB === 3) return labels.summary4bbbc || '';
    if (countB === 2) return labels.summary4bbcc || '';
    if (countB === 1) return labels.summary4bccc || '';
    return labels.summary4cccc || '';
  };

  const resolveInitialSku = function(config, root) {
    try {
      const url = new URL(window.location.href);
      const variantId = url.searchParams.get('variant');
      if (variantId) {
        const urlSku = findVariantById(config, root, variantId);
        if (urlSku) return urlSku;
      }
    } catch (_error) {}

    const currentSku = getCurrentSkuFromSelect(config, root);
    if (currentSku) return currentSku;

    if (isVariantSelectable(config, config.defaultSku)) return config.defaultSku;
    if (isVariantExisting(config, config.defaultSku)) return config.defaultSku;

    const firstAvailable = Object.keys(config.variants || {}).find((sku) => isVariantSelectable(config, sku));
    if (firstAvailable) return firstAvailable;

    const firstExisting = Object.keys(config.variants || {}).find((sku) => isVariantExisting(config, sku));
    return firstExisting || config.defaultSku;
  };

  window.FermentationPickerShared = {
    JAR_COUNT,
    DEFAULT_JARS,
    DOMHelper,
    getScopeRoot,
    getMergedContainer,
    getVariantSelect,
    parseConfig,
    getVariantData,
    isVariantExisting,
    isVariantSelectable,
    isJarSoldOut,
    isStampferSoldOut,
    normalizeState,
    encodeSku,
    decodeSku,
    resolveStateFromSku,
    areStatesEquivalent,
    findVariantById,
    getCurrentSkuFromSelect,
    getCurrentVariant,
    updateVariantBySku,
    getMainKeyBySku,
    getMainSkuForState,
    resolveVariantSkuForState,
    resolvePreferredState,
    resolveDefaultCarouselState,
    resolveCarouselStateFromCurrent,
    getJarSummaryLabel,
    resolveInitialSku,
    clamp
  };

  const FermentationPicker = {
    getContext: function(root = null) {
      const scope = root || getScopeRoot();
      const merged = getMergedContainer(scope);
      const container = merged ? merged.querySelector('#custom-picker.fermentation-variant-picker') : scope.querySelector('#custom-picker.fermentation-variant-picker');
      return { root: scope, merged, container };
    },

    getHostScope: function(container, root = null) {
      return (container && container.closest('product-info')) || root || getScopeRoot();
    },

    getConfig: function(container) {
      const merged = container ? (container.closest('.fermentation-merged-picker') || container) : getMergedContainer();
      return parseConfig(merged);
    },

    getCurrentState: function(root = null) {
      const ctx = this.getContext(root);
      if (!ctx.container) return null;
      if (ctx.container._fermentationState) return normalizeState(ctx.container._fermentationState);

      const config = this.getConfig(ctx.container);
      const sku = config ? getCurrentSkuFromSelect(config, this.getHostScope(ctx.container, ctx.root)) : '';
      return sku ? resolveStateFromSku(config, sku) : null;
    },

    createJarPreview: function(config, jarType) {
      const imageMap = config && config.assets ? config.assets.jarImagesSmall : {};
      const imageUrl = imageMap && imageMap[jarType] ? imageMap[jarType] : '';
      const label = jarType === 'C' ? config.ui.jarCLabel : config.ui.jarBLabel;

      const preview = DOMHelper.createElement('div', {
        className: 'selection-preview media-wrapper media-wrapper--small'
      });

      if (!imageUrl) return preview;

      const image = DOMHelper.createElement('img', {
        attributes: {
          src: imageUrl,
          alt: label,
          loading: 'eager',
          decoding: 'async'
        },
        styles: {
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '50%'
        }
      });

      preview.setAttribute('loading', 'true');
      const markLoaded = () => preview.removeAttribute('loading');
      if (image.complete) {
        markLoaded();
      } else {
        image.addEventListener('load', () => (window.requestIdleCallback ? window.requestIdleCallback(markLoaded, { timeout: 150 }) : setTimeout(markLoaded)), { once: true });
        image.addEventListener('error', markLoaded, { once: true });
        if (typeof image.decode === 'function') {
          image.decode().then(markLoaded).catch(() => {});
        }
        window.setTimeout(markLoaded, 1800);
      }

      preview.appendChild(image);
      return preview;
    },

    getJarDescription: function(config, index, jarType) {
      const label = jarType === 'C'
        ? (config.ui.jarCDescription || config.ui.jarCLabel)
        : (config.ui.jarBDescription || config.ui.jarBLabel);
      return label;
    },

    createConfigureButton: function(config, index) {
      const buttonLabel = String(config.ui.configureJarSlot || '')
        .replace('{{ number }}', String(index + 1));

      return DOMHelper.createElement('button', {
        className: 'configure-button always-visible',
        html: `
          <svg class="edit-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
          </svg>
          <span>${buttonLabel}</span>
        `,
        attributes: {
          type: 'button',
          tabindex: '-1',
          'aria-hidden': 'true'
        }
      });
    },

    createTrioItem: function(config, jarType, index) {
      const item = DOMHelper.createElement('div', {
        className: 'trio-item fermentation-custom__jar-row',
        attributes: {
          'data-trio-index': index,
          role: 'button',
          tabindex: '0'
        }
      });

      item.appendChild(this.createJarPreview(config, jarType));
      item.appendChild(DOMHelper.createElement('div', {
        className: 'trio-description',
        text: this.getJarDescription(config, index, jarType)
      }));
      item.appendChild(this.createConfigureButton(config, index));
      return item;
    },

    renderJarRows: function(overview, config, state) {
      if (!overview) return;
      const nextSignature = state.noJars ? 'nojars' : state.jars.join('');
      if (overview.dataset.rowsSignature === nextSignature) return;

      const fragment = document.createDocumentFragment();
      if (!state.noJars) {
        state.jars.forEach((jarType, index) => {
          fragment.appendChild(this.createTrioItem(config, jarType === 'C' ? 'C' : 'B', index));
        });
      }

      overview.replaceChildren(fragment);
      overview.dataset.rowsSignature = nextSignature;
    },

    animateJarPanel: function(container, expanded) {
      const panel = container.querySelector('[data-jar-panel]');
      if (!panel) return;

      const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = 420;
      const isInitialRender = !panel.hasAttribute('data-expanded');

      if (panel._fermentationJarPanelTimer) {
        clearTimeout(panel._fermentationJarPanelTimer);
        panel._fermentationJarPanelTimer = null;
      }

      if (reducedMotion) {
        panel.hidden = !expanded;
        panel.classList.toggle('is-collapsed', !expanded);
        panel.style.height = '';
        panel.style.overflow = '';
        panel.style.transition = '';
        panel.style.willChange = '';
        panel.dataset.expanded = expanded ? 'true' : 'false';
        return;
      }

      if (isInitialRender) {
        panel.hidden = !expanded;
        panel.classList.toggle('is-collapsed', !expanded);
        panel.style.height = '';
        panel.style.overflow = '';
        panel.style.transition = '';
        panel.style.willChange = '';
        panel.dataset.expanded = expanded ? 'true' : 'false';
        return;
      }

      const currentExpanded = panel.dataset.expanded !== 'false';
      if (currentExpanded === expanded && !panel.style.height) {
        panel.hidden = !expanded;
        panel.classList.toggle('is-collapsed', !expanded);
        return;
      }

      panel.hidden = false;
      const startHeight = panel.getBoundingClientRect().height;

      panel.style.transition = 'none';
      panel.style.height = `${startHeight}px`;
      panel.style.overflow = 'hidden';
      panel.style.willChange = 'height, opacity, transform';
      panel.classList.toggle('is-collapsed', !expanded);

      const targetHeight = expanded ? panel.scrollHeight : 0;

      requestAnimationFrame(() => {
        panel.style.transition = [
          `height ${duration}ms cubic-bezier(0.22, 1, 0.36, 1)`,
          `opacity ${Math.round(duration * 0.72)}ms ease`,
          `transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1)`
        ].join(', ');
        panel.style.height = `${targetHeight}px`;
      });

      panel._fermentationJarPanelTimer = setTimeout(() => {
        panel.hidden = !expanded;
        panel.classList.toggle('is-collapsed', !expanded);
        panel.style.height = '';
        panel.style.overflow = '';
        panel.style.transition = '';
        panel.style.willChange = '';
        panel.dataset.expanded = expanded ? 'true' : 'false';
      }, duration + 90);
    },

    updateSelectionOverview: function(container, config, state) {
      const overview = container.querySelector('[data-selection-overview]');
      const panel = container.querySelector('[data-jar-panel]');
      if (!overview) return;
      if (panel && panel._fermentationJarContentTimer) {
        clearTimeout(panel._fermentationJarContentTimer);
        panel._fermentationJarContentTimer = null;
      }

      const shouldExpand = !state.noJars;
      if (shouldExpand) {
        const previousState = normalizeState(container._fermentationPreviousState || state);
        const previousHadRows = !previousState.noJars && !!overview.querySelector('.trio-item[data-trio-index]');

        if (!previousHadRows) {
          this.renderJarRows(overview, config, state);
        } else {
          for (let index = 0; index < JAR_COUNT; index += 1) {
            const existingItem = overview.querySelector(`.trio-item[data-trio-index="${index}"]`);
            const jarType = state.jars[index] === 'C' ? 'C' : 'B';
            const previousJarType = previousState.jars[index] === 'C' ? 'C' : 'B';

            if (!existingItem) {
              overview.appendChild(this.createTrioItem(config, jarType, index));
              continue;
            }

            if (jarType !== previousJarType) {
              const existingPreview = existingItem.querySelector('.selection-preview');
              const existingDescription = existingItem.querySelector('.trio-description');

              if (existingPreview && existingDescription) {
                const newPreview = this.createJarPreview(config, jarType);
                const newDescription = this.getJarDescription(config, index, jarType);

                existingPreview.classList.add('content-exit');
                existingDescription.classList.add('content-exit');

                setTimeout(() => {
                  existingPreview.replaceChildren(...Array.from(newPreview.childNodes));
                  if (newPreview.hasAttribute('loading')) {
                    existingPreview.setAttribute('loading', 'true');
                  } else {
                    existingPreview.removeAttribute('loading');
                  }
                  existingDescription.textContent = newDescription;

                  existingPreview.classList.remove('content-exit');
                  existingDescription.classList.remove('content-exit');
                  existingPreview.classList.add('content-enter');
                  existingDescription.classList.add('content-enter');

                  setTimeout(() => {
                    existingPreview.classList.remove('content-enter');
                    existingDescription.classList.remove('content-enter');
                  }, 300);
                }, 50);
              }
            }
          }

          overview.dataset.rowsSignature = state.jars.join('');
        }
      }

      this.animateJarPanel(container, shouldExpand);

      if (!shouldExpand) {
        if (panel) {
          panel._fermentationJarContentTimer = setTimeout(() => {
            if (container._fermentationState && normalizeState(container._fermentationState).noJars) {
              overview.replaceChildren();
              overview.dataset.rowsSignature = 'nojars';
            }
          }, 240);
        }
      }

      container._fermentationPreviousState = normalizeState(state);
    },

    updateModalChoices: function(container, config, state) {
      const modalTitle = container.querySelector('[data-jar-modal-title]');
      const editingIndex = clamp(Number(container.dataset.editingJarIndex || 0), 0, JAR_COUNT - 1);
      if (modalTitle) {
        modalTitle.textContent = `${config.ui.jarModalTitle} · ${config.ui.jarSlotPrefix} ${editingIndex + 1}`;
      }

      const soldOutByChoice = { B: isJarSoldOut(config, 'B'), C: isJarSoldOut(config, 'C') };
      // Fail-open guard: if both appear sold out at once, keep both selectable.
      if (soldOutByChoice.B && soldOutByChoice.C) {
        soldOutByChoice.B = false;
        soldOutByChoice.C = false;
      }

      qsa('[data-modal-jar-choice]', container).forEach((choiceButton) => {
        const choice = String(choiceButton.dataset.modalJarChoice || '').toUpperCase();
        const isSelected = !state.noJars && state.jars[editingIndex] === choice;
        const soldOut = choice === 'C' ? soldOutByChoice.C : soldOutByChoice.B;
        const soldOutBadge = choiceButton.querySelector('.soldout-badge');

        choiceButton.classList.toggle('selected', isSelected);
        choiceButton.classList.toggle('is-soldout', soldOut);
        choiceButton.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        choiceButton.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
        if (soldOutBadge) soldOutBadge.hidden = !soldOut;
      });
    },

    updateSummary: function(container, config, state) {
      const summary = container.querySelector('[data-current-summary]');
      const summaryWrap = container.querySelector('[data-current-summary-wrap]');
      const summaryPrice = container.querySelector('[data-current-summary-price]');
      if (!summary) return;

      const lines = [];
      const ui = config && config.ui ? config.ui : {};
      const activeSku = resolveVariantSkuForState(config, state);
      const activeVariant = getVariantData(config, activeSku);

      if (ui.itemLids) lines.push({ text: ui.itemLids, fixed: true });
      if (ui.itemWeights) lines.push({ text: ui.itemWeights, fixed: true });

      if (state.noJars) {
        if (ui.noJarsLabel) lines.push({ text: ui.noJarsLabel, fixed: false });
      } else {
        const countB = state.jars.filter((jar) => jar === 'B').length;
        const countC = state.jars.filter((jar) => jar === 'C').length;

        if (countB > 0) lines.push({ text: `${countB}× ${ui.jarBDescription || ui.jarBLabel}`, fixed: false });
        if (countC > 0) lines.push({ text: `${countC}× ${ui.jarCDescription || ui.jarCLabel}`, fixed: false });
      }

      lines.push({
        text: state.tamper === 1 ? ui.itemWithTamper : ui.itemWithoutTamper,
        fixed: false
      });

      summary.replaceChildren();
      const visibleLines = lines.filter((line) => line && line.text);
      visibleLines.forEach((line, index) => {
          const previousLine = index > 0 ? visibleLines[index - 1] : null;
          const startsSelection = !line.fixed && previousLine && previousLine.fixed;
          const item = DOMHelper.createElement('li', {
            className: `fermentation-picker__summary-line${line.fixed ? ' is-fixed' : ''}${startsSelection ? ' is-selection-start' : ''}`
          });

          item.appendChild(DOMHelper.createElement('span', {
            className: 'fermentation-picker__summary-line-text',
            text: line.text
          }));

          if (line.fixed && ui.summaryFixedBadge) {
            item.appendChild(DOMHelper.createElement('span', {
              className: 'fermentation-picker__summary-line-badge',
              text: ui.summaryFixedBadge
            }));
          }

          summary.appendChild(item);
        });

      const hasLines = visibleLines.length > 0;
      summary.hidden = !hasLines;
      if (summaryWrap) summaryWrap.hidden = !hasLines;
      if (summaryPrice) {
        const priceText = activeVariant && activeVariant.formattedPrice ? activeVariant.formattedPrice : '';
        summaryPrice.textContent = priceText;
        summaryPrice.hidden = !priceText;
      }
    },

    updateTamper: function(container, config, state) {
      const tamperCheckbox = container.querySelector('[data-tamper-checkbox]');
      const tamperRow = container.querySelector('[data-tamper-row]');
      const unavailableNote = container.querySelector('[data-stampfer-unavailable-note]');
      const stampferSoldOut = isStampferSoldOut(config);

      if (tamperCheckbox) {
        tamperCheckbox.checked = state.tamper === 1;
        const canToggle = !!resolvePreferredState(config, {
          ...state,
          tamper: state.tamper === 1 ? 0 : 1
        });
        tamperCheckbox.disabled = stampferSoldOut || !canToggle;
      }

      if (tamperRow) {
        tamperRow.classList.toggle('checkbox-active', !!(tamperCheckbox && tamperCheckbox.checked && !stampferSoldOut));
        tamperRow.classList.toggle('is-disabled', stampferSoldOut);
      }

      if (unavailableNote) unavailableNote.hidden = !stampferSoldOut;
    },

    render: function(container, config, state) {
      if (!container || !config || !state) return;

      const normalizedState = normalizeState(state);
      const noJarsCheckbox = container.querySelector('[data-no-jars-checkbox]');
      if (noJarsCheckbox) {
        noJarsCheckbox.checked = normalizedState.noJars;
        const toNoJarsPossible = !!resolvePreferredState(config, { ...normalizedState, noJars: true });
        const toWithJarsPossible = !!resolvePreferredState(config, { ...normalizedState, noJars: false });
        noJarsCheckbox.disabled = normalizedState.noJars ? !toWithJarsPossible : !toNoJarsPossible;
      }

      this.updateSelectionOverview(container, config, normalizedState);
      this.updateModalChoices(container, config, normalizedState);
      this.updateSummary(container, config, normalizedState);
      this.updateTamper(container, config, normalizedState);

      const warning = container.querySelector('[data-no-jars-warning]');
      if (warning) warning.hidden = !normalizedState.noJars;
    },

    syncFromSku: function(container, config, sku, options = {}) {
      const decoded = resolveStateFromSku(config, sku);
      if (!decoded) return false;

      const state = normalizeState(decoded);
      container._fermentationState = state;
      this.render(container, config, state);

      if (options.updateVariant) {
        updateVariantBySku(resolveVariantSkuForState(config, state), this.getHostScope(container, options.root));
      }
      return true;
    },

    applyState: function(container, config, desiredState, options = {}) {
      if (!container || !config || !desiredState) return false;
      const resolvedState = resolvePreferredState(config, desiredState);
      if (!resolvedState) return false;

      container._fermentationState = resolvedState;
      this.render(container, config, resolvedState);

      if (options.updateVariant !== false) {
        updateVariantBySku(resolveVariantSkuForState(config, resolvedState), this.getHostScope(container, options.root));
      }
      return true;
    },

    openJarModal: function(container, config, index) {
      const modal = container.querySelector('[data-jar-modal]');
      const modalContent = container.querySelector('[data-jar-modal-content]');
      if (!modal || !modalContent) return;

      container.dataset.editingJarIndex = String(clamp(index, 0, JAR_COUNT - 1));
      this.updateModalChoices(container, config, normalizeState(container._fermentationState));

      modal.classList.add('active');
      document.body.style.overflow = 'hidden';

      modalContent.style.transition = 'none';
      if (window.innerWidth <= 480) {
        modalContent.style.transform = 'translateY(100%)';
      } else {
        modalContent.style.transform = 'translateY(20px)';
      }
      modalContent.style.opacity = '0';
      void modalContent.offsetWidth;
      modalContent.style.transition = 'transform 0.45s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.45s ease';
      modalContent.style.transform = 'translateY(0)';
      modalContent.style.opacity = '1';

      const selectedOption = modal.querySelector('.jar-option.selected');
      if (selectedOption) selectedOption.scrollIntoView({ block: 'center', inline: 'nearest' });

      this.initSwipeToClose(container, modalContent);
    },

    closeJarModal: function(container) {
      const modal = container.querySelector('[data-jar-modal]');
      const modalContent = container.querySelector('[data-jar-modal-content]');
      if (!modal || !modalContent) return;

      modal.classList.remove('active');
      modalContent.style.transition = 'transform 0.45s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.45s ease';
      if (window.innerWidth <= 480) {
        modalContent.style.transform = 'translateY(100%)';
      } else {
        modalContent.style.transform = 'translateY(20px)';
      }
      modalContent.style.opacity = '0';

      setTimeout(() => {
        document.body.style.overflow = '';
      }, 450);
    },

    initSwipeToClose: function(container, element) {
      if (!container || !element || element._swipeHandlers) return;

      let startY = 0;
      let currentY = 0;
      let isDragging = false;

      if (!element.querySelector('.swipe-handle-area')) {
        element.prepend(DOMHelper.createElement('div', { className: 'swipe-handle-area' }));
      }

      const modal = container.querySelector('[data-jar-modal]');

      const handleTouchStart = (event) => {
        const touchY = event.touches[0].clientY;
        const rect = element.getBoundingClientRect();
        const isTopArea = touchY - rect.top < 60;
        if (!isTopArea) return;

        startY = event.touches[0].clientY;
        currentY = startY;
        isDragging = true;
        element.style.transition = 'none';
      };

      const handleTouchMove = (event) => {
        if (!isDragging) return;

        currentY = event.touches[0].clientY;
        const deltaY = currentY - startY;
        if (deltaY <= 0) return;

        element.style.transform = `translateY(${deltaY}px)`;
        if (modal) {
          const opacity = Math.max(1 - (deltaY / 300), 0.5);
          modal.style.backgroundColor = `rgba(0, 0, 0, ${opacity * 0.5})`;
        }
        event.preventDefault();
      };

      const handleTouchEnd = () => {
        if (!isDragging) return;
        isDragging = false;
        element.style.transition = 'transform 0.45s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.45s ease';

        const deltaY = currentY - startY;
        if (deltaY > 100) {
          this.closeJarModal(container);
          if (modal) modal.style.backgroundColor = '';
          return;
        }

        element.style.transform = 'translateY(0)';
        element.style.opacity = '1';
        if (modal) modal.style.backgroundColor = '';
      };

      element.addEventListener('touchstart', handleTouchStart, { passive: false });
      element.addEventListener('touchmove', handleTouchMove, { passive: false });
      element.addEventListener('touchend', handleTouchEnd);

      element._swipeHandlers = { handleTouchStart, handleTouchMove, handleTouchEnd };
    },

    setStampferModalOpen: function(container, isOpen) {
      const modal = container.querySelector('[data-stampfer-modal]');
      const trigger = container.querySelector('[data-open-stampfer-modal]');
      if (!modal) return;

      modal.classList.toggle('active', !!isOpen);
      container.dataset.stampferModalOpen = isOpen ? 'true' : 'false';
      if (trigger) trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    },

    bindEvents: function(container, config, root) {
      if (!container || container.dataset.fermentationCustomEventsBound === 'true') return;
      container.dataset.fermentationCustomEventsBound = 'true';

      const hostScope = this.getHostScope(container, root);

      container.addEventListener('click', (event) => {
        const trioItem = event.target.closest('.trio-item[data-trio-index]');
        if (trioItem && container.contains(trioItem)) {
          const trioIndex = Number(trioItem.dataset.trioIndex || 0);
          this.openJarModal(container, config, trioIndex);
          return;
        }

        const jarChoice = event.target.closest('[data-modal-jar-choice]');
        if (jarChoice && container.contains(jarChoice)) {
          if (jarChoice.classList.contains('is-soldout')) return;
          const currentState = normalizeState(container._fermentationState);
          const jarIndex = clamp(Number(container.dataset.editingJarIndex || 0), 0, JAR_COUNT - 1);
          const nextJars = currentState.jars.slice();
          nextJars[jarIndex] = String(jarChoice.dataset.modalJarChoice || '').toUpperCase() === 'C' ? 'C' : 'B';
          const applied = this.applyState(container, config, {
            ...currentState,
            noJars: false,
            jars: nextJars
          }, { root: hostScope });
          if (applied) this.closeJarModal(container);
          return;
        }

        if (event.target.closest('[data-close-jar-modal]')) {
          this.closeJarModal(container);
          return;
        }

        const jarModal = event.target.closest('[data-jar-modal]');
        if (jarModal && event.target === jarModal) {
          this.closeJarModal(container);
          return;
        }

        if (event.target.closest('[data-open-stampfer-modal]')) {
          this.setStampferModalOpen(container, true);
          return;
        }

        if (event.target.closest('[data-close-stampfer-modal]')) {
          this.setStampferModalOpen(container, false);
          return;
        }

        const stampferModal = event.target.closest('[data-stampfer-modal]');
        if (stampferModal && event.target === stampferModal) {
          this.setStampferModalOpen(container, false);
        }
      });

      container.addEventListener('keydown', (event) => {
        const trioItem = event.target.closest('.trio-item[data-trio-index]');
        if (trioItem && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          const trioIndex = Number(trioItem.dataset.trioIndex || 0);
          this.openJarModal(container, config, trioIndex);
          return;
        }

        if (event.key === 'Escape') {
          if (container.dataset.stampferModalOpen === 'true') this.setStampferModalOpen(container, false);
          this.closeJarModal(container);
        }
      });

      const noJarsCheckbox = container.querySelector('[data-no-jars-checkbox]');
      if (noJarsCheckbox) {
        noJarsCheckbox.addEventListener('change', () => {
          const currentState = normalizeState(container._fermentationState);
          const applied = this.applyState(container, config, {
            ...currentState,
            noJars: !!noJarsCheckbox.checked
          }, { root: hostScope });
          if (!applied) noJarsCheckbox.checked = currentState.noJars;
        });
      }

      const tamperCheckbox = container.querySelector('[data-tamper-checkbox]');
      if (tamperCheckbox) {
        tamperCheckbox.addEventListener('change', () => {
          if (tamperCheckbox.disabled) return;
          const currentState = normalizeState(container._fermentationState);
          const nextTamper = tamperCheckbox.checked ? 1 : 0;
          const applied = this.applyState(container, config, {
            ...currentState,
            tamper: nextTamper
          }, { root: hostScope });
          if (!applied) tamperCheckbox.checked = currentState.tamper === 1;
        });
      }

      const select = getVariantSelect(hostScope);
      if (select && !select.dataset.fermentationCustomBound) {
        select.dataset.fermentationCustomBound = 'true';
        select.addEventListener('change', () => {
          const sku = getCurrentSkuFromSelect(config, hostScope);
          if (sku) this.syncFromSku(container, config, sku);
        });
      }
    },

    initContainer: function(container, root) {
      if (!container || container.dataset.fermentationPickerInitialized === 'true') return;

      const config = this.getConfig(container);
      if (!config) return;

      container.dataset.fermentationPickerInitialized = 'true';
      container.dataset.editingJarIndex = container.dataset.editingJarIndex || '0';

      this.bindEvents(container, config, root);

      const hostScope = this.getHostScope(container, root);
      const initialSku = resolveInitialSku(config, hostScope);
      const initialDecoded = resolveStateFromSku(config, initialSku) || resolveDefaultCarouselState(config);
      const initialState = resolvePreferredState(config, initialDecoded) || resolveDefaultCarouselState(config);
      container._fermentationState = initialState;
      container._fermentationPreviousState = normalizeState(initialState);
      this.render(container, config, initialState);
    },

    init: function(root = null) {
      const scope = root || getScopeRoot();
      const containers = qsa('#custom-picker.fermentation-variant-picker', scope);
      containers.forEach((container) => this.initContainer(container, scope));
    },

    updateUI: function(updateVariant = true, root = null) {
      const ctx = this.getContext(root);
      if (!ctx.container) return;

      const config = this.getConfig(ctx.container);
      if (!config) return;

      const hostScope = this.getHostScope(ctx.container, ctx.root);
      const currentSku = getCurrentSkuFromSelect(config, hostScope);
      if (currentSku) {
        this.syncFromSku(ctx.container, config, currentSku);
        return;
      }

      const currentState = normalizeState(ctx.container._fermentationState || resolveDefaultCarouselState(config));
      this.applyState(ctx.container, config, currentState, {
        root: hostScope,
        updateVariant: updateVariant
      });
    },

    syncAllFromSelect: function(root = null) {
      const scope = root || getScopeRoot();
      const containers = qsa('#custom-picker.fermentation-variant-picker', scope);
      containers.forEach((container) => {
        const config = this.getConfig(container);
        if (!config) return;

        const hostScope = this.getHostScope(container, scope);
        const sku = getCurrentSkuFromSelect(config, hostScope);
        if (!sku) return;
        this.syncFromSku(container, config, sku, { root: hostScope, updateVariant: false });
      });
    }
  };

  window.FermentationPicker = FermentationPicker;

  if (document.readyState !== 'loading') {
    FermentationPicker.init();
  } else {
    document.addEventListener('DOMContentLoaded', () => FermentationPicker.init());
  }

  document.addEventListener('quickview:loaded', () => FermentationPicker.init(getScopeRoot()));
  document.addEventListener('product-info:loaded', () => FermentationPicker.init(getScopeRoot()));
  document.addEventListener('variantChange', () => FermentationPicker.syncAllFromSelect(getScopeRoot()));
})();
